import type { UserRole } from '@/types/common';

export type FlowId = 'device-setup' | 'user-creation' | 'access-group-creation' | 'bulk-user-import';

export type RoleText = string | Partial<Record<UserRole, string>>;

export type FlowFieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'password'
  | 'number'
  | 'date'
  | 'select'
  | 'segmented'
  | 'card-picker'
  | 'list-single'
  | 'list-multi';

export interface FlowFieldOption {
  value: string;
  label: string;
  description?: string;
}

export type FlowListIcon = 'device-controller';

export interface FlowFieldSchema<TDraft> {
  id: string;
  type: FlowFieldType;
  label: RoleText;
  required?: boolean;
  placeholder?: string;
  options?: FlowFieldOption[] | ((draft: TDraft) => FlowFieldOption[]);
  visibleWhen?: (draft: TDraft) => boolean;
  aiField?: string;
  heading?: string;
  subheading?: string;
  /** Shows device-controller tile icons on `list-single` rows (e.g. model picker). */
  listIcon?: FlowListIcon;
  /** Filterable checkbox list (access group readers). */
  searchable?: boolean;
  searchPlaceholder?: string;
  derivePatch?: (value: string, draft: TDraft) => Partial<TDraft>;
}

export interface FlowStepSchema<TDraft> {
  id: string;
  instruction?: RoleText;
  layout?: 'grid' | 'stack' | 'dual-column' | 'credential';
  fields: FlowFieldSchema<TDraft>[];
  nextLabel?: string;
  backLabel?: string;
}

export interface ApprovalRowSchema<TDraft> {
  key: string;
  value: (draft: TDraft) => string;
  aiField?: string;
}

export interface SuccessDefinition<TDraft, TResult> {
  bannerText: (result: TResult, draft: TDraft) => string;
  noteText: string;
}

export interface GuidedFlowDefinition<TDraft, TStep extends string, TResult> {
  id: FlowId;
  formTitle: string;
  systemLabelPrefix: string;
  /** Single-step flows (access group): hide the stepper strip. */
  hideStepper?: boolean;
  /** Fixed-size Figma form card (835×641) for compact single-step flows. */
  compactFormCard?: boolean;
  /** @deprecated Use exclusiveSavingScreen. Keep form visible while saving. */
  persistFormDuringSaving?: boolean;
  /** On Confirm: hide thread/form and show only the saving UI until complete. */
  exclusiveSavingScreen?: boolean;
  /** Which surface renders during the form phase. Defaults to `form`. */
  formSurface?: 'form' | 'bulk-review';
  steps: ReadonlyArray<{ id: TStep; label: string }>;
  schemas: Record<TStep, FlowStepSchema<TDraft>>;
  approvalRows: ApprovalRowSchema<TDraft>[];
  success: SuccessDefinition<TDraft, TResult>;
}

export function resolveRoleText(text: RoleText | undefined, role: UserRole): string | undefined {
  if (!text) return undefined;
  if (typeof text === 'string') return text;
  return text[role] ?? text.operator ?? text.novice;
}
